import { Box, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DateTime } from 'luxon'
import { useTranslate } from 'ra-core'
import { Filter, FunctionField, List, ReferenceField, SelectColumnsButton, useGetIdentity } from 'react-admin'

import config from './config'
import MarkerField from './field'
import { IncidentReportsCategoryFilter, IncidentReportsOwnershipFilter, IncidentReportsSeverityFilter } from './filter'
import IncidentReportFormLayout from './form'
import { IncidentCategoryField } from './IncidentCategorySelectInput'
import { IncidentReportStatusField, IncidentReportStatusSelector } from './IncidentReportStatusUI'
import { IncidentOwnershipField } from './OwnershipSelectInput'
import { IncidentSeverityField, SEVERITY_VISUALS } from './SeveritySelectInput'
import AdvancedDatagridConfigurable from '../../components/AdvancedDatagridConfigurable'
import AdvancedExportButton from '../../components/AdvancedExportButton'
import CompactList from '../../components/CompactList'
import NoteField from '../../components/NoteField'
import ReferenceValue from '../../components/ReferenceValue'
import StaticText from '../../components/StaticText'
import VehicleIconField from '../../components/VehicleIconField'
import { INCIDENT_REPORT_STATUS_REPORTED, INCIDENT_REPORT_STATUS_VALIDATED } from '../../config/damages'
import { OPS_USER_ROLE_LEASING_COMPANY, SYSTEM_PERMISSION_READ } from '../../config/permissions'
import { useCommonStyles } from '../../config/theme'
import { useResourcePermissions } from '../../domain/permissions'
import { isAllowed } from '../../utils'
import { parseDateAsDateTime } from '../../utils/dates'
import { useCurrentAccountSelectors, useSingleOrganisationFilterDefaultValues } from '../accounts/hooks'
import { AdvancedCreateButton } from '../common/create'
import { GenericListLayout, ListActions } from '../common/list'
import damageBlueprintsConfig from '../damageBlueprints/config'
import HubField from '../hubs/field'
import HubsFilter from '../hubs/filter'
import OrganisationField from '../organisations/field'
import OrganisationsFilter from '../organisations/filter'
import usersConfig from '../users/config'
import VehiclesFilter from '../vehicles/filter'

const isOpenDamageReport = (record) =>
  record?.status === INCIDENT_REPORT_STATUS_REPORTED || record?.status === INCIDENT_REPORT_STATUS_VALIDATED

const CreatedOnListField = ({ source = 'created_on' }) => {
  const translate = useTranslate()

  return (
    <FunctionField
      source={source}
      render={(record) => {
        const value = record?.[source]
        const dt = parseDateAsDateTime(value)

        if (!value || !dt.isValid) return <Typography variant="body2">n/a</Typography>

        const dateTimeLabel = dt.toLocaleString(DateTime.DATETIME_SHORT)
        const tooltipLabel = `${translate('resources.incident-reports.fields.created_on')}: ${dateTimeLabel}`

        return (
          <Tooltip title={tooltipLabel}>
            <Typography component="span" variant="body2" sx={{ display: 'block', textAlign: 'center' }}>
              {dateTimeLabel}
            </Typography>
          </Tooltip>
        )
      }}
    />
  )
}

const VehicleReferenceField = ({ source = 'vehicle_id' }) => (
  <ReferenceField reference="vehicles" source={source} link="show">
    <FunctionField
      render={(record) => {
        const designation = record?.designation
        const vehicleDetails = [record?.model, record?.brand].filter(Boolean).join(' • ')

        if (!designation && !vehicleDetails) return <PendingValuePlaceholder />

        return (
          <Box
            sx={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.2,
              minWidth: 0,
              px: 0.5,
              py: 0.35,
              borderRadius: 1.5,
              transition: 'background-color 120ms ease',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
              },
              '&:hover .vehicleDesignation': {
                textDecorationColor: (theme) => alpha(theme.palette.primary.main, 0.32),
              },
            }}
          >
            <Typography
              className="vehicleDesignation"
              variant="body2"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.06em',
                lineHeight: 1.1,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                color: 'primary.main',
                textDecoration: 'underline',
                textDecorationThickness: '0.08em',
                textUnderlineOffset: '0.16em',
                textDecorationColor: 'transparent',
                transition: 'text-decoration-color 120ms ease',
              }}
            >
              {designation || '-'}
            </Typography>
            {vehicleDetails ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.2, whiteSpace: 'nowrap', maxWidth: '100%', textOverflow: 'ellipsis' }}
              >
                {vehicleDetails}
              </Typography>
            ) : null}
          </Box>
        )
      }}
    />
  </ReferenceField>
)

const PendingValuePlaceholder = () => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Typography component="span" variant="body2" color="text.secondary">
        -
      </Typography>
    </Box>
  )
}

const CategoryListField = ({ source = 'category' }) => (
  <FunctionField
    source={source}
    render={(record) =>
      record?.[source] ? <IncidentCategoryField value={record[source]} /> : <PendingValuePlaceholder />
    }
  />
)

const SeverityBadgeField = ({ source = 'severity' }) => {
  return (
    <FunctionField
      source={source}
      render={(record) => {
        const severity = record?.[source]
        if (!severity) return <PendingValuePlaceholder />

        const visual = SEVERITY_VISUALS[severity] || { color: '#64748b' }

        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.45,
              borderRadius: 999,
              backgroundColor: (theme) => alpha(visual.color, theme.palette.mode === 'dark' ? 0.2 : 0.1),
              border: '1px solid',
              borderColor: (theme) => alpha(visual.color, theme.palette.mode === 'dark' ? 0.3 : 0.16),
              mx: 'auto',
            }}
          >
            <IncidentSeverityField value={severity} />
          </Box>
        )
      }}
    />
  )
}

const OwnershipListField = ({ source = 'ownership' }) => (
  <FunctionField
    source={source}
    render={(record) =>
      record?.[source] ? <IncidentOwnershipField value={record[source]} /> : <PendingValuePlaceholder />
    }
  />
)

const IncidentReportsFilters = (props) => (
  <Filter {...props}>
    <OrganisationsFilter alwaysOn />
    <HubsFilter alwaysOn />
    <VehiclesFilter alwaysOn />
    <IncidentReportsCategoryFilter alwaysOn />
    <IncidentReportsSeverityFilter alwaysOn />
    <IncidentReportsOwnershipFilter alwaysOn />
  </Filter>
)

export const IncidentReportsListLayout = ({ excluded = [], ...props }) => {
  const [hasReadForUsers] = useResourcePermissions(usersConfig.name, SYSTEM_PERMISSION_READ)
  const { hasSingleOrganisation, hasSingleHub } = useCurrentAccountSelectors()
  const translate = useTranslate()

  const commonClasses = useCommonStyles()

  return (
    <GenericListLayout
      action={<IncidentReportStatusSelector />}
      compactListLayout={
        <CompactList
          linkType="show"
          referencesSources={{ [usersConfig.options.referenceKey]: 'reporter_id' }}
          icon={
            <ReferenceValue reference="vehicles" target={(v) => <VehicleIconField record={v} />} emptyText={null} />
          }
          title={() => (
            <>
              <Typography component="span" variant="body1" sx={commonClasses.inline} color="textPrimary">
                <ReferenceValue reference="organisations" />
              </Typography>
              {isAllowed(excluded, 'vehicles') && (
                <Typography component="span" variant="body1" sx={commonClasses.inline} color="textSecondary">
                  {' • '}
                  <ReferenceValue reference="vehicles" />
                </Typography>
              )}
            </>
          )}
          body={() => (
            <>
              <StaticText>{translate('resources.hubs.name', 1)}: </StaticText>
              <ReferenceValue reference="hubs" emptyText="n/a" />
              {hasReadForUsers && (
                <>
                  <br />
                  <StaticText>{'resources.incident-reports.fields.reporter_id'} </StaticText>
                  <ReferenceValue reference="users" source="reporter_id" />
                </>
              )}
              <br />
              <StaticText>{'resources.incident-reports.fields.category'}: </StaticText>
              <CategoryListField source="category" />
              <br />
              <StaticText>{'resources.incident-reports.fields.severity'}: </StaticText>
              <SeverityBadgeField source="severity" />
              <br />
              <StaticText>{'resources.incident-reports.fields.ownership'}: </StaticText>
              <OwnershipListField source="ownership" />
              <br />
              <StaticText>{'resources.incident-reports.fields.status'}: </StaticText>
              <IncidentReportStatusField source="status" />
            </>
          )}
        />
      }
      regularListLayout={
        <AdvancedDatagridConfigurable
          bulkActionButtons={false}
          rowClick="show"
          omit={['organisation_id', 'hub_id']}
          sx={{
            '& .RaDatagrid-headerCell': { textAlign: 'center' },
            '& .RaDatagrid-headerCellContent': { justifyContent: 'center' },
            '& .RaDatagrid-rowCell': { textAlign: 'center' },
            '& .RaDatagrid-rowCell > *': { marginLeft: 'auto', marginRight: 'auto' },
          }}
        >
          <IncidentReportStatusField source="status" />
          {isAllowed(excluded, 'organisations') && !hasSingleOrganisation && <OrganisationField sortable={false} />}
          {isAllowed(excluded, 'hubs') && !hasSingleHub && <HubField sortable={false} />}
          {isAllowed(excluded, 'vehicles') && <VehicleReferenceField source="vehicle_id" />}
          <CategoryListField source="category" />
          <SeverityBadgeField source="severity" />
          <OwnershipListField source="ownership" />
          <MarkerField source={damageBlueprintsConfig.options.referenceKey} containerWidth={200} markerSize={16} />
          {hasReadForUsers && <ReferenceField source="reporter_id" reference="users" emptyText="n/a" />}
          <NoteField source="note" sortable={false} />
          <CreatedOnListField source="created_on" />
        </AdvancedDatagridConfigurable>
      }
      {...props}
    />
  )
}

export default () => {
  const { identity } = useGetIdentity()
  const translate = useTranslate()
  const filterDefaultValues = useSingleOrganisationFilterDefaultValues(config.options.defaultFilterValues)

  return (
    <List
      empty={false}
      actions={
        <ListActions>
          {identity && identity.role !== OPS_USER_ROLE_LEASING_COMPANY && <AdvancedExportButton />}
          <AdvancedCreateButton
            sx={{
              '& .MuiDialog-paper': {
                width: 'min(92vw, 755px)',
                maxWidth: '755px',
              },
            }}
          >
            <IncidentReportFormLayout />
          </AdvancedCreateButton>
          <SelectColumnsButton />
        </ListActions>
      }
      title={translate('resources.incident-reports.name', 2)}
      sort={config.options.defaultSort}
      filters={<IncidentReportsFilters />}
      filterDefaultValues={filterDefaultValues}
      exporter={false}
    >
      <IncidentReportsListLayout />
    </List>
  )
}
